import { useEffect, useMemo } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import { appointmentPatientsAtom } from '@/modules/patientList/atoms';
import {
  type ImagePathType,
  type QualityIssueType,
  useImageQualityAssessment as useFetchImageQualityAssessment,
  useSubmitImageQualityAssessment,
} from '@/react-query/useImageQualityAssessment';
import { usePreferences } from '@/react-query/usePreferences';
import { getSelectedImagePath } from '@/utils/cornerstone/useCornerstone';

import { useTrackImageIndex } from '../findingViewer/imageBrowser/helpers';
import { ImageQualityAssessmentOverlay } from '../ImageQualityAssessmentOverlay';
import type { HDVersionType } from '../imageToolbar/atoms';
import {
  imageQualityAssessmentOverlayOpenAtom,
  imageQualityAssessmentOverlayPositionAtom,
  imageQualityAssessmentPatientIdsAtom,
  showSegmentationAtom,
} from '../imageToolbar/atoms';

export interface UseImageQualityAssessmentProps {
  isThumbnail: boolean;
  imageLoadStatus: string;
  activeImage?: any;
  enabledHD?: HDVersionType | undefined;
}

export const useImageQualityAssessment = ({
  isThumbnail,
  imageLoadStatus,
  activeImage,
  enabledHD,
}: UseImageQualityAssessmentProps) => {
  // State Variables
  const [isOverlayOpen, setIsOverlayOpen] = useAtom(
    imageQualityAssessmentOverlayOpenAtom,
  );
  const setOverlayPosition = useSetAtom(
    imageQualityAssessmentOverlayPositionAtom,
  );
  const appointmentPatients = useAtomValue(appointmentPatientsAtom);
  const setPatientIds = useSetAtom(imageQualityAssessmentPatientIdsAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);

  // Custom Hooks
  const { data: prefs } = usePreferences();
  const history = useHistory();
  const nextImageIds = useTrackImageIndex();
  const submitAssessment = useSubmitImageQualityAssessment();

  // Local Scope Variables
  const shouldEnableImageQualityAssessment = Boolean(
    prefs?.enableImageQualityAssessment,
  );
  const shouldPreferDisplayOverSharpened = prefs?.preferDisplayOverSharpened;
  const isSubmitting = submitAssessment.isLoading;

  // Helper function to determine which path field is being used
  const getPathType = (
    image: any,
    preferDisplayOverSharpened?: boolean,
    hdVersion?: HDVersionType,
  ): ImagePathType | undefined => {
    if (!image) return undefined;

    // Get the path being displayed using the same logic as the viewer
    const selectedPath = getSelectedImagePath(
      image,
      !!preferDisplayOverSharpened,
      undefined, // dentalPhotoThumbnail
      undefined, // disableAdjustmentForPanos
      hdVersion,
    );

    // Determine which field this path corresponds to
    if (selectedPath === image.sharpenedPath) {
      return 'sharpenedPath' as ImagePathType;
    } else if (selectedPath === image.originalPath) {
      return 'originalPath' as ImagePathType;
    }
    return 'path' as ImagePathType;
  };

  // Compute which path field is being used based on HQ flag and activeImage
  const pathType = useMemo(
    () => getPathType(activeImage, shouldPreferDisplayOverSharpened, enabledHD),
    [activeImage, shouldPreferDisplayOverSharpened, enabledHD],
  );

  // Query for existing assessment
  const { data: existingAssessment } = useFetchImageQualityAssessment({
    imageId: activeImage?.imageManifestId?.toString(),
    pathType: pathType || undefined,
  });

  const shouldShowOverlay =
    !isThumbnail &&
    shouldEnableImageQualityAssessment &&
    imageLoadStatus === 'success';

  // Check if there's a next image
  const hasNextImage = Boolean(
    nextImageIds?.currentIndex !== null &&
      nextImageIds?.currentIndex !== undefined &&
      nextImageIds?.totalImages !== null &&
      nextImageIds?.totalImages !== undefined &&
      nextImageIds?.currentIndex < nextImageIds?.totalImages - 1,
  );

  // Handlers
  const handleQualityAssessmentSubmit = async (data: {
    rating: number;
    issueTypes: QualityIssueType[];
    note: string;
  }) => {
    if (!activeImage || !activeImage.imageManifestId || !pathType) return;

    try {
      await submitAssessment.mutateAsync({
        imageId: activeImage.imageManifestId.toString(),
        pathType: pathType!,
        data: {
          qualityRating: data.rating,
          qualityIssueTypes: data.issueTypes,
          note: data.note,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit image quality assessment:', error);
    }
  };

  const handleOpenOverlay = () => {
    if (isOverlayOpen) {
      // If overlay is open, close it
      setIsOverlayOpen(false);
    } else {
      // If overlay is closed, open it and reset position
      setOverlayPosition({ x: 80, y: 80 });
      setIsOverlayOpen(true);
      // Disable patient view when image quality assessment overlay opens
      setShowSegmentation(false);
    }
  };

  const handleSubmitAndNext = async (data: {
    rating: number;
    issueTypes: QualityIssueType[];
    note: string;
  }) => {
    if (!activeImage || !activeImage.imageManifestId || !pathType) return;

    try {
      await submitAssessment.mutateAsync({
        imageId: activeImage.imageManifestId.toString(),
        pathType: pathType!,
        data: {
          qualityRating: data.rating,
          qualityIssueTypes: data.issueTypes,
          note: data.note,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      // Navigate to next image
      if (nextImageIds?.next && activeImage?.imageId) {
        const newRoute = history.location.pathname.replace(
          activeImage.imageId,
          nextImageIds.next,
        );
        history.push(newRoute);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit image quality assessment:', error);
    }
  };

  const handleCloseOverlay = () => {
    setIsOverlayOpen(false);
  };

  // useEffect Hooks
  useEffect(() => {
    // Extract imagingPatientIds from appointment patients
    const imagingPatientIds = appointmentPatients
      .filter((patient) => patient.imagingPatientId)
      .map((patient) => patient.imagingPatientId!);
    setPatientIds(imagingPatientIds);
  }, [appointmentPatients, setPatientIds]);

  // Open overlay by default when on single image view (not thumbnail) and preference is enabled
  useEffect(() => {
    if (shouldShowOverlay) {
      setIsOverlayOpen(true);
      // Disable patient view when image quality assessment overlay opens
      setShowSegmentation(false);
    }
    // Close overlay when navigating away from single image view
    return () => {
      if (isThumbnail || !shouldShowOverlay) {
        setIsOverlayOpen(false);
      }
    };
  }, [
    activeImage?.imageManifestId,
    isThumbnail,
    imageLoadStatus,
    shouldEnableImageQualityAssessment,
    shouldShowOverlay,
    setIsOverlayOpen,
    setShowSegmentation,
  ]);

  // Return Statement
  const QualityAssessmentWidget = shouldShowOverlay ? (
    <ImageQualityAssessmentOverlay
      imageId={activeImage?.imageManifestId?.toString()}
      onSubmit={handleQualityAssessmentSubmit}
      onSubmitAndNext={handleSubmitAndNext}
      onClose={handleCloseOverlay}
      existingAssessment={existingAssessment}
      isOpen={isOverlayOpen}
      hasNextImage={hasNextImage}
      isSubmitting={isSubmitting}
    />
  ) : null;

  return {
    QualityAssessmentWidget,
    shouldEnableImageQualityAssessment,
    handleOpenOverlay,
  };
};
